import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { CalendarOff, Plus, Trash2, Loader2, Check, X } from 'lucide-react';
import { formatDate, getInitials } from '../../lib/utils';
import { toast } from 'sonner';

export default function LeaveManagement() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instructors, setInstructors] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ instructor_id: '', date_from: '', date_to: '', reason: '', leave_type: 'full_day' });
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const isInstructor = user?.role === 'instructor';

  const fetchLeaves = () => api.get('/api/leaves').then(res => setLeaves(res.data.leaves || []));

  useEffect(() => {
    fetchLeaves().finally(() => setLoading(false));
    if (isAdmin) api.get('/api/users', { params: { role: 'instructor', limit: 50 } }).then(res => setInstructors(res.data.users || []));
  }, []);

  const handleCreate = async () => {
    if (!form.date_from) { toast.error('Start date required'); return; }
    try {
      await api.post('/api/leaves', {
        ...form,
        date_to: form.date_to || form.date_from,
        instructor_id: isInstructor ? undefined : form.instructor_id,
      });
      toast.success('Leave applied');
      setCreateOpen(false);
      setForm({ instructor_id: '', date_from: '', date_to: '', reason: '', leave_type: 'full_day' });
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleApprove = async (id) => {
    await api.put(`/api/leaves/${id}`, { status: 'approved' });
    toast.success('Leave approved');
    fetchLeaves();
  };

  const handleReject = async (id) => {
    await api.put(`/api/leaves/${id}`, { status: 'rejected' });
    toast.success('Leave rejected');
    fetchLeaves();
  };

  const handleDelete = async (id) => {
    await api.delete(`/api/leaves/${id}`);
    toast.success('Leave deleted');
    fetchLeaves();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{isInstructor ? 'My Leaves' : 'Leave Management'}</h1>
          <p className="text-muted-foreground mt-1">{isInstructor ? 'Apply and track your leaves' : 'Manage instructor leaves'}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="apply-leave-button">
          <Plus className="h-4 w-4 mr-2" />{isInstructor ? 'Apply Leave' : 'Add Leave'}
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="pt-5">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : leaves.length === 0 ? (
            <div className="text-center py-12">
              <CalendarOff className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No leaves recorded</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead>Instructor</TableHead>}
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaves.map(l => (
                  <TableRow key={l.id}>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{getInitials(l.instructor_name)}</AvatarFallback></Avatar>
                          {l.instructor_name}
                        </div>
                      </TableCell>
                    )}
                    <TableCell>{formatDate(l.date_from)}</TableCell>
                    <TableCell>{formatDate(l.date_to)}</TableCell>
                    <TableCell><Badge variant="secondary">{l.leave_type?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{l.reason || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={l.status === 'approved' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : l.status === 'rejected' ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-amber-50 text-amber-800 border-amber-200'}>
                        {l.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {isAdmin && l.status === 'pending' && (
                          <>
                            <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handleApprove(l.id)}><Check className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleReject(l.id)}><X className="h-3 w-3" /></Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(l.id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">{isInstructor ? 'Apply Leave' : 'Add Leave'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {isAdmin && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Instructor</label>
                <Select value={form.instructor_id} onValueChange={v => setForm({...form, instructor_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select instructor" /></SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="z-[200]">
                    {instructors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium mb-1.5 block">From</label><Input type="date" value={form.date_from} onChange={e => setForm({...form, date_from: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1.5 block">To</label><Input type="date" value={form.date_to} onChange={e => setForm({...form, date_to: e.target.value})} /></div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Leave Type</label>
              <Select value={form.leave_type} onValueChange={v => setForm({...form, leave_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="full_day">Full Day</SelectItem><SelectItem value="half_day">Half Day</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium mb-1.5 block">Reason</label><Textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} rows={2} placeholder="Optional" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
